import type {
  AdminAgreementActionsResponse,
  AuditEventResponse,
  TransferConfirmationResponse,
} from '@api/generated/types.gen';

/** Цвет тега статуса договора (AntD `Tag`); неизвестный статус — default. */
export const AGREEMENT_STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'blue',
  REPAYING: 'blue',
  CLOSED: 'success',
  OVERDUE: 'error',
  DISPUTE: 'error',
  ENFORCEMENT: 'error',
  NOT_DISBURSED: 'orange',
  AWAITING_FEE: 'orange',
  AWAITING_DISBURSEMENT: 'orange',
  CANCELLED: 'default',
  DECLINED: 'default',
  EXPIRED: 'default',
};

/** Вид передачи в матрице подтверждений. */
export type TransferKind = 'DISBURSEMENT' | 'REPAYMENT';

/** Сторона договора. */
export type PartyRole = 'CREDITOR' | 'DEBTOR';

export const TRANSFER_KINDS: TransferKind[] = ['DISBURSEMENT', 'REPAYMENT'];
export const PARTY_ROLES: PartyRole[] = ['CREDITOR', 'DEBTOR'];

/**
 * Матрица подтверждений: вид передачи × сторона → запись подтверждения.
 * Отсутствие записи = сторона не подтвердила — главный ответ для поддержки.
 */
export type ConfirmationMatrix = Record<
  TransferKind,
  Partial<Record<PartyRole, TransferConfirmationResponse>>
>;

/** Раскладывает плоский список подтверждений в матрицу «вид × сторона». */
export function buildConfirmationMatrix(
  confirmations: TransferConfirmationResponse[],
): ConfirmationMatrix {
  const matrix: ConfirmationMatrix = { DISBURSEMENT: {}, REPAYMENT: {} };
  for (const c of confirmations) {
    if ((c.kind === 'DISBURSEMENT' || c.kind === 'REPAYMENT') && c.partyRole) {
      matrix[c.kind][c.partyRole] = c;
    }
  }
  return matrix;
}

/**
 * Действия, ключевые для разбора споров поддержкой, — подсвечиваются в ленте.
 */
export const KEY_ACTIONS = new Set([
  'CONFIRM_REPAYMENT',
  'MARK_PAID',
  'CONFIRM_PAID',
  'AUTO_CONFIRM_PAID',
  'CONFIRM_DISBURSEMENT',
  'CANCEL',
]);

/**
 * Уникальные идентификаторы акторов из всего ответа — для пакетной
 * расшифровки в телефоны через `GET /api/admin/users/{id}` (акторов обычно 2–3).
 */
export function collectActorIds(data: AdminAgreementActionsResponse): string[] {
  const ids = new Set<string>();
  for (const c of data.transferConfirmations ?? []) {
    if (c.confirmedBy) ids.add(c.confirmedBy);
  }
  for (const i of data.installments ?? []) {
    if (i.markedBy) ids.add(i.markedBy);
    if (i.confirmedBy) ids.add(i.confirmedBy);
  }
  for (const e of data.events ?? []) {
    if (e.actorId) ids.add(e.actorId);
  }
  return [...ids];
}

/** Типы сущностей, реально встречающиеся в ленте, — в порядке появления. */
export function collectEntityTypes(events: AuditEventResponse[]): string[] {
  const types: string[] = [];
  for (const e of events) {
    if (e.entityType && !types.includes(e.entityType)) types.push(e.entityType);
  }
  return types;
}

/** Результат проверки связности hash-chain журнала аудита на клиенте. */
export type AuditChainCheck = {
  /** `true`, если ни одна цепочка не разорвана. */
  valid: boolean;
  /** Индексы (в исходном массиве) событий, на которых цепочка рвётся. */
  brokenIndexes: Set<number>;
};

function eventTime(e: AuditEventResponse): number {
  if (!e.createdAt) return 0;
  const ts = new Date(e.createdAt).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

/**
 * Проверяет связность hash-chain журнала аудита.
 *
 * Цепочка ведётся отдельно по каждой паре `entityType + entityId` (у договора,
 * документа, передач — свои цепочки, в ленте они перемешаны по времени).
 * Для каждой группы события сортируются по `createdAt` (стабильно, при
 * равенстве — исходный порядок) и проверяется: у первой записи
 * `prevHash == null`, у каждой следующей `prevHash === hash` предыдущей.
 *
 * Хеш самой записи не пересчитывается — формула завязана на серверную
 * канонизацию полей; клиент проверяет только связность. Событие, у которого
 * `prevHash` не совпал, считается точкой разрыва.
 */
export function verifyAuditChains(events: AuditEventResponse[]): AuditChainCheck {
  type Indexed = { event: AuditEventResponse; index: number };
  const groups = new Map<string, Indexed[]>();
  events.forEach((event, index) => {
    const key = `${event.entityType ?? ''}:${event.entityId ?? ''}`;
    const list = groups.get(key);
    if (list) list.push({ event, index });
    else groups.set(key, [{ event, index }]);
  });

  const brokenIndexes = new Set<number>();
  for (const group of groups.values()) {
    const ordered = [...group].sort((a, b) => eventTime(a.event) - eventTime(b.event));
    let prevHash: string | undefined;
    ordered.forEach(({ event, index }, pos) => {
      const expected = pos === 0 ? undefined : prevHash;
      if ((event.prevHash ?? undefined) !== expected) brokenIndexes.add(index);
      prevHash = event.hash;
    });
  }
  return { valid: brokenIndexes.size === 0, brokenIndexes };
}

/** Сокращённый хеш для показа в ленте: первые 8 hex-символов. */
export function shortHash(hash: string): string {
  return hash.slice(0, 8);
}

/**
 * Достаёт имя файла из заголовка `Content-Disposition`.
 *
 * Понимает `filename="…"`, `filename=…` и RFC 5987 `filename*=UTF-8''…`
 * (последний в приоритете). Возвращает `null`, если заголовка нет или имя
 * не найдено — вызывающий подставляет фолбэк.
 */
export function parseContentDispositionFilename(header: string | null | undefined): string | null {
  if (!header) return null;
  const ext = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (ext?.[1]) {
    try {
      return decodeURIComponent(ext[1].trim());
    } catch {
      /* невалидный percent-encoding — падаем на обычный filename */
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/.exec(header);
  const name = (plain?.[1] ?? plain?.[2] ?? '').trim();
  return name === '' ? null : name;
}
